// =============================================
// runLock — re-entrant browser-only mutex dla standalone vs map_content
// =============================================
// Standalone mode w promptLoader (setStandaloneMode) jest globalną flagą JS —
// gdy true, loadPrompt('section_generation') zwraca section_generation_standalone
// (bez nakazu linków wewnętrznych). Race: jeśli mapa odpali section_generation
// w trakcie standalone, dostanie standalone-only prompt i NIE wstawi linków.
//
// Ten lock GWARANTUJE że oba kanały nie biegną równolegle w przeglądarce:
//   - tryAcquire('standalone') zwraca {ok:false, blockedBy:'map_content'} gdy mapa pracuje
//   - tryAcquire('map_content') zwraca {ok:false, blockedBy:'standalone'} gdy standalone pracuje
//
// Re-entrant: ten sam channel może acquire wielokrotnie (counter). Każdy acquire
// musi mieć match release. activeCount = 0 → activeChannel = null (channel zwolniony).
//
// Scope: browser-only. Worker (server-side) ma własny process, własny promptLoader,
// nie współdzieli flagi — race tam fizycznie nie wystąpi.
// =============================================

export type LockChannel = "map_content" | "standalone";

let activeChannel: LockChannel | null = null;
let activeCount = 0;

export interface AcquireResult {
  ok: boolean;
  blockedBy?: LockChannel;
}

/**
 * Spróbuj zająć lock dla danego kanału.
 * - Wolny lock (activeChannel === null) → ok, set channel, count=1
 * - Ten sam channel (re-entrant) → ok, count++
 * - Inny channel zajmuje → blocked, zwróć blockedBy
 */
export function tryAcquire(channel: LockChannel): AcquireResult {
  if (activeChannel !== null && activeChannel !== channel) {
    return { ok: false, blockedBy: activeChannel };
  }
  activeChannel = channel;
  activeCount++;
  return { ok: true };
}

/**
 * Zwolnij lock dla danego kanału. Decrement counter; gdy 0 → kanał wolny.
 * Bezpieczne wielokrotne wywołanie dla NIE-aktywnego channela (no-op).
 */
export function release(channel: LockChannel): void {
  if (activeChannel !== channel) return;
  activeCount = Math.max(0, activeCount - 1);
  if (activeCount === 0) {
    activeChannel = null;
  }
}

/** Diagnostic: który kanał aktualnie zajmuje lock (null = wolny). */
export function getActiveChannel(): LockChannel | null {
  return activeChannel;
}

/** Diagnostic: ile razy aktualny channel został acquire (re-entry depth). */
export function getActiveCount(): number {
  return activeCount;
}
